import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { SupportCaseService } from "@/services/support/supportCaseService";
import {
  CreateSupportCaseRequest,
  SupportCaseDetails,
  SupportMessage,
} from "@/types/support";
type MySupportCasesListCache = {
  cases?: SupportCaseDetails[];
  total?: number;
  page?: number;
  totalPages?: number;
};
export type MySupportCasesListFilters = {
  status?: string;
  page?: number;
  limit?: number;
};
export function markUserSupportCaseReadInCache(
  queryClient: QueryClient,
  caseId: string,
) {
  queryClient.setQueriesData<MySupportCasesListCache>(
    { queryKey: ["mySupportCases"] },
    (old) => {
      if (!old?.cases) return old;
      return {
        ...old,
        cases: old.cases.map((c) =>
          c.id === caseId ? { ...c, hasUnread: false } : c,
        ),
      };
    },
  );
  void queryClient.invalidateQueries({ queryKey: ["supportUnreadSummary"] });
}
export const useSupportCase = (
  userId?: string,
  selectedCaseId?: string | null,
  listFilters?: MySupportCasesListFilters,
) => {
  const queryClient = useQueryClient();
  const mySupportCasesQuery = useQuery({
    queryKey: ["mySupportCases", userId, listFilters],
    queryFn: async () =>
      SupportCaseService.getMySupportCases(userId!, listFilters),
    enabled: !!userId,
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: "always",
  });
  const supportCaseQuery = useQuery({
    queryKey: ["supportCase", "user", selectedCaseId],
    queryFn: async () => SupportCaseService.getSupportCase(selectedCaseId!),
    enabled: !!selectedCaseId,
    staleTime: 0,
    refetchOnMount: "always",
  });
  useEffect(() => {
    if (!supportCaseQuery.isSuccess || !selectedCaseId) return;
    markUserSupportCaseReadInCache(queryClient, selectedCaseId);
  }, [
    supportCaseQuery.isSuccess,
    supportCaseQuery.dataUpdatedAt,
    selectedCaseId,
    queryClient,
  ]);
  const createSupportCaseMutation = useMutation({
    mutationFn: (request: CreateSupportCaseRequest) =>
      SupportCaseService.createSupportCase(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mySupportCases"] });
    },
  });
  const sendMessageMutation = useMutation({
    mutationFn: ({
      caseId,
      content,
    }: {
      caseId: string;
      content: string;
    }) => SupportCaseService.sendCaseMessage(caseId, content),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["supportCase", "user", variables.caseId],
      });
      queryClient.invalidateQueries({ queryKey: ["mySupportCases"] });
    },
  });
  const uploadDocumentMutation = useMutation({
    mutationFn: ({ caseId, file }: { caseId: string; file: File }) =>
      SupportCaseService.uploadCaseDocument(caseId, file),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["supportCase", "user", variables.caseId],
      });
    },
  });
  const deleteDocumentMutation = useMutation({
    mutationFn: ({
      caseId,
      documentId,
    }: {
      caseId: string;
      documentId: string;
    }) => SupportCaseService.deleteCaseDocument(caseId, documentId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["supportCase", "user", variables.caseId],
      });
    },
  });
  return {
    mySupportCases: mySupportCasesQuery.data?.cases || [],
    mySupportCasesMeta: {
      total: mySupportCasesQuery.data?.total || 0,
      page: mySupportCasesQuery.data?.page || 1,
      totalPages: mySupportCasesQuery.data?.totalPages || 0,
    },
    supportCase: supportCaseQuery.data as SupportCaseDetails | undefined,
    isLoadingMyCases: mySupportCasesQuery.isLoading,
    isLoadingCase: supportCaseQuery.isLoading,
    myCasesError: mySupportCasesQuery.error?.message || null,
    caseError: supportCaseQuery.error?.message || null,
    createSupportCase: createSupportCaseMutation.mutate,
    createSupportCaseAsync: createSupportCaseMutation.mutateAsync,
    sendMessage: sendMessageMutation.mutate,
    sendMessageAsync: sendMessageMutation.mutateAsync,
    uploadDocumentAsync: uploadDocumentMutation.mutateAsync,
    deleteDocumentAsync: deleteDocumentMutation.mutateAsync,
    isCreating: createSupportCaseMutation.isPending,
    isSendingMessage: sendMessageMutation.isPending,
    isUploadingDocument: uploadDocumentMutation.isPending,
    isDeletingDocument: deleteDocumentMutation.isPending,
    createError: createSupportCaseMutation.error?.message || null,
    sendMessageError: sendMessageMutation.error?.message || null,
    createSuccess: createSupportCaseMutation.isSuccess,
    sendMessageSuccess: sendMessageMutation.isSuccess,
    refetchMySupportCases: mySupportCasesQuery.refetch,
    refetchSupportCase: supportCaseQuery.refetch,
    resetCreate: createSupportCaseMutation.reset,
    resetSendMessage: sendMessageMutation.reset,
  };
};
export type { SupportMessage };